import React, { useContext, useRef, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, Plus } from 'lucide-react'
import { AuthContext } from '../../context/AuthContext'
import { useCustomFetch } from '../../hooks/useCustomFetch'
import useOutsideClick from '../../hooks/useOutsideClick'
import { DashboardContext } from '../../context/DashboardContext'
import { CircleChevronDown } from '../icons/CircleChevronDown'

export function WorkspaceSelection() {
  const { user, setUser } = useContext(AuthContext)
  const { workspaces } = useContext(DashboardContext)
  const [openMenu, setOpenMenu] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const workspaceSelectorRef = useRef(null)
  const searchInputRef = useRef(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const customFetch = useCustomFetch()

  useOutsideClick(workspaceSelectorRef, () => {
    if (workspaceSelectorRef) {
      setOpenMenu(false)
      setSearchQuery('')
    }
  })

  // Focus search input when menu opens
  React.useEffect(() => {
    if (openMenu && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }, [openMenu])

  async function handleChangeActiveWorkspace(newActiveWorkspace) {
    if (user.active_workspace === newActiveWorkspace) return
    try {
      setUser((prevUser) => ({ ...prevUser, active_workspace: newActiveWorkspace }))
      const response = await customFetch('/updateActiveWorkspace', 'PUT', { workspaceId: newActiveWorkspace })
      if (response.error) console.error(response.error)
    } catch (err) {
      console.error(err)
    }
  }

  const planName = useMemo(() => {
    if (
      !workspaces ||
      !user.active_workspace ||
      !Object.keys(workspaces).length ||
      !Object.keys(user.active_workspace).length
    )
      return ''
    let text = workspaces[user.active_workspace].plan_id
    if (!text) return ''
    text = text.replace('USD-', '').replaceAll('-', ' ')
    return text
  }, [workspaces, user.active_workspace])

  const planPrice = useMemo(() => {
    if (
      !workspaces ||
      !user.active_workspace ||
      !Object.keys(workspaces).length ||
      !Object.keys(user.active_workspace).length
    )
      return 0
    if (!workspaces[user.active_workspace].plan_price) return 0
    return (workspaces[user.active_workspace].plan_price / 100).toFixed(2)
  }, [workspaces, user.active_workspace])

  const billingPeriodUnit = useMemo(() => {
    if (
      !workspaces ||
      !user.active_workspace ||
      !Object.keys(workspaces).length ||
      !Object.keys(user.active_workspace).length
    )
      return ''
    if (!workspaces[user.active_workspace].billing_period_unit) return ''
    const capitalizeFirstLetter = (text) => text.charAt(0).toUpperCase() + text.slice(1)
    return capitalizeFirstLetter(workspaces[user.active_workspace].billing_period_unit)
  }, [workspaces, user.active_workspace])

  // Filter workspaces by search query
  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery.trim()) {
      return Object.keys(user.workspaces || {}).filter((workspace) => Boolean(workspaces[workspace]))
    }
    return Object.keys(user.workspaces || {})
      .filter((workspace) => Boolean(workspaces[workspace]))
      .filter((workspace) => workspaces[workspace].name.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [searchQuery, user.workspaces, workspaces])

  if (
    !workspaces ||
    !user.active_workspace ||
    !Object.keys(workspaces).length ||
    !Object.keys(user.active_workspace).length
  ) {
    return (
      <div className="mt-[40px] w-[600px] px-[16px] py-[8px] bg-white rounded shadow border border-grey-200 relative">
        <button className="mx-2 flex items-center w-full justify-between">
          <p className="opacity-80 text-black text-[13px] font-bold font-['Manrope'] leading-none">Loading...</p>
          <div className="mr-2">
            <CircleChevronDown isOpen={openMenu} />
          </div>
        </button>
      </div>
    )
  }

  return (
    <div
      className="mt-[40px] w-[600px] px-[16px] py-[8px] bg-white rounded shadow border border-grey-200 relative"
      ref={workspaceSelectorRef}
    >
      <button className="mx-2 flex items-center w-full justify-between" onClick={() => setOpenMenu(!openMenu)}>
        <p className="opacity-80 text-black text-[13px] font-bold font-['Manrope'] leading-none">
          {workspaces[user.active_workspace].name}
        </p>
        {planName && planPrice && billingPeriodUnit && (
          <p className="opacity-80 text-[#40424A] text-[13px] font-semibold font-['Manrope'] leading-none">
            {planName} (${planPrice} / {billingPeriodUnit})
          </p>
        )}
        <div className="mr-2">
          <CircleChevronDown isOpen={openMenu} />
        </div>
      </button>
      {openMenu && (
        <div
          className="absolute w-[600px] left-[-1px] top-8 px-[16px] py-[8px] bg-white shadow rounded-b border-x border-grey-200 z-10 flex flex-col"
          style={{ maxHeight: 400 }}
        >
          {/* Search Input */}
          <div className="mb-2">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search workspaces..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 text-[13px] border border-grey-200 rounded bg-white text-black placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#FF4A8E]"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Scrollable workspace list */}
          <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
            {filteredWorkspaces.length > 0 ? (
              filteredWorkspaces.map((workspace) => (
                <button
                  key={workspace}
                  className="p-1 flex items-center gap-[8px] my-[5px] w-full hover:bg-gray-50 rounded"
                  onClick={() => handleChangeActiveWorkspace(workspace)}
                >
                  {user.active_workspace === workspace ? (
                    <>
                      <Check className="w-[17px] h-[17px] text-[#FF4A8E]" strokeWidth={2} />
                      <p className="opacity-80 text-black text-[13px] font-bold font-['Manrope'] leading-none">
                        {workspaces[workspace].name}
                      </p>
                    </>
                  ) : (
                    <p className="ml-[25px] opacity-80 text-black text-[13px] font-medium font-['Manrope'] leading-none">
                      {workspaces[workspace].name}
                    </p>
                  )}
                </button>
              ))
            ) : (
              <div className="px-2 py-4 text-center">
                <p className="text-[13px] text-gray-500">No workspaces found</p>
              </div>
            )}
          </div>

          {/* Add Workspace button */}
          <button
            className="border-t w-[568px] pt-[15px] pb-[8px] mt-2 border-grey-200 flex grow items-center justify-start gap-[14px]"
            onClick={() => navigate(`/workspace/create?plan_id=${searchParams.get('plan_id')}`)}
          >
            <Plus className="w-[13px] h-[13px] text-[#FF4A8E]" />
            <p className="opacity-80 text-black text-[13px] font-medium font-['Manrope'] leading-none">Add Workspace</p>
          </button>
        </div>
      )}
    </div>
  )
}
